import { cloneElement, isValidElement, useId } from "react";
import { twJoin } from "tailwind-merge";

import type { DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";
import { Info } from "./Info";
import { Inline } from "./Inline";
import { Spread } from "./Spread";
import { Stack } from "./Stack";

interface FormFieldProps {
  label: string;
  description?: string;
  tooltip?: string;
  required?: boolean;
  hideLabel?: boolean;
  actions?: React.ReactNode;
  children: React.ReactNode;
  id?: string;
  layout?: "vertical" | "horizontal";
}

export function FormField({
  label,
  description,
  tooltip,
  required,
  hideLabel,
  actions,
  children,
  id,
  layout = "vertical",
}: FormFieldProps): React.ReactNode {
  const randomId = useId();
  const idOverride = id || randomId;
  const descriptionId = description ? `${idOverride}-description` : undefined;

  return (
    <div
      className={twJoin(
        "flex",
        layout === "vertical" ? "flex-col" : "items-center",
        ((label && !hideLabel) || actions || description) && "gap-sm",
      )}
    >
      <Stack gap="xs">
        <Spread space="sm">
          {label && (
            <FormFieldLabel
              label={label}
              htmlFor={idOverride}
              hideLabel={hideLabel}
              isRequired={required}
              tooltip={tooltip}
            />
          )}
          {actions}
        </Spread>
        {description && (
          <p id={descriptionId} className="text-caption text-text-secondary">
            {description}
          </p>
        )}
      </Stack>
      {/* Assume children accept these accessibility attributes */}
      {isValidElement(children)
        ? cloneElement(children, { id: idOverride, "aria-describedby": descriptionId } as any)
        : children}
    </div>
  );
}

/** Full-width wrapper for compound controls that receive field accessibility attributes as a group. */
export function FormFieldControl({
  children,
  ref,
  ...nativeProps
}: {
  children: React.ReactNode;
} & Omit<DesignSystemElementProps<"div">, "children"> &
  DesignSystemRef<HTMLDivElement>): React.ReactNode {
  return (
    <div {...nativeProps} ref={ref} className="w-full">
      {children}
    </div>
  );
}

type FormFieldLabelProps = {
  label: string;
  htmlFor?: string;
  tooltip?: string;
  hideLabel?: boolean;
  isRequired?: boolean;
};

function FormFieldLabel({ label, htmlFor, hideLabel, isRequired, tooltip }: FormFieldLabelProps) {
  const formattedLabel = isRequired ? `${label}*` : label;

  return (
    <Inline gap="sm" align="center">
      <label htmlFor={htmlFor} className={twJoin("text-caption-emphasis", hideLabel && "sr-only")}>
        {formattedLabel}
      </label>
      {tooltip && <Info tooltip={tooltip} />}
    </Inline>
  );
}
